// Exam API Service Layer
import CONF from '@/Config.js'
import axios from 'axios'
import { formatLocalToUtc } from '@/utils/datetime'

// 직접 API 호출을 처리하는 서비스 클래스
class ExamService {
  
  // API 호출 헬퍼 함수
  async makeRequest(config) {
    const token = localStorage.getItem("access_token");

    // API URL 결정
    let apiUrl;
    if (config.api_url === CONF.ACHIEVE_API_URL) {
      // ACHIEVE_API_URL은 환경변수에서 직접 사용
      apiUrl = CONF.ACHIEVE_API_URL || 'http://localhost:8169';
    } else {
      apiUrl = config.api_url || CONF.API_URL;
    }

    const fullUrl = apiUrl + config.url;

    try {
      const response = await axios({
        method: config.method,
        url: fullUrl,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...config.headers
        },
        data: config.data,
        params: config.params,
        responseType: config.responseType
      });

      return response;
    } catch (error) {
      // 순환 참조 에러 방지를 위한 간소화된 에러 객체 생성
      const simplifiedError = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method
        }
      };
      console.error('API Request Error:', simplifiedError);
      throw error;
    }
  }

  // 대면 시험 수동 시작 신호 전송
  async manualStartSignal(examId, classroomIdx) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: `/exams/${examId}/manual-start-signal`,
        api_url: CONF.ACHIEVE_API_URL,
        params: {
          classroom_idx: classroomIdx
        }
      });

      if (response.status === 200 || response.status === 201) {
        return response.data
      }
    } catch (error) {
      console.error('Error sending manual start signal:', error)
      throw error
    }
  }

  // 시험 목록 조회
  async fetchExams(params = {}) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: '/exams/',
        api_url: CONF.ACHIEVE_API_URL,
        params
      });

      if (response.status === 200) {
        const payload = response.data
        //console.log('[fetchExams] payload.items:', payload.items);
        return payload
      }
    } catch (error) {
      console.error('Error fetching exams:', error)
      throw error
    }
  }

  // 특정 시험 조회
  async fetchExam(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/exams/${examId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error fetching exam:', error)
      throw error
    }
  }

  // 시험 생성
  async createExam(examData) {
    try {
      // 종료시간 미입력 정책 반영: startTime + duration(분)으로 계산
      // 시간은 로컬 벽시각 문자열 그대로 전달, timezone은 별도 필드로 전달
      const startLocal = (examData.startTime || '').toString()
      const addMinutesLocal = (localStr, minutes) => {
        try {
          if (!localStr) return ''
          const [d, t] = localStr.split('T')
          const [yy, mm, dd] = (d || '').split('-').map(n => parseInt(n, 10))
          const [HH, MM = '0'] = (t || '').split(':')
          if (!yy || !mm || !dd || HH == null) return ''
          const base = new Date(yy, (mm || 1) - 1, dd, parseInt(HH, 10) || 0, parseInt(MM, 10) || 0, 0, 0)
          base.setMinutes(base.getMinutes() + Number(minutes || 0))
          const pad = (n) => String(n).padStart(2, '0')
          const y = base.getFullYear()
          const M = pad(base.getMonth() + 1)
          const D = pad(base.getDate())
          const h = pad(base.getHours())
          const m = pad(base.getMinutes())
          return `${y}-${M}-${D}T${h}:${m}`
        } catch { return '' }
      }
      // 정책: duration이 있으면 항상 startTime + duration(분)으로 재계산하여 일관성 확보
      const endLocal = (startLocal && examData?.duration)
        ? addMinutesLocal(startLocal, examData.duration)
        : String(examData.endTime || '')

      const timezone = examData.timeZone || (examData.settings && examData.settings.timeZone) || 'Asia/Seoul'

      // 현지 시간을 UTC로 변환
      const startUtc = formatLocalToUtc(startLocal, timezone)
      const endUtc = formatLocalToUtc(endLocal, timezone)

      const payload = {
        ...examData,
        startTimeUTC: startUtc || startLocal,
        endTimeUTC  : endUtc || endLocal,
        // 서버 호환을 위해 루트 키로 timezone도 함께 전달 (settings.timeZone를 복제)
        timezone
      }
      // 기존 필드 제거 (백엔드에서 사용 안함)
      delete payload.startTime
      delete payload.endTime

      //console.log('createExam payload:', payload);
      const response = await this.makeRequest({
        method: 'post',
        url: '/exams/',
        api_url: CONF.ACHIEVE_API_URL,
        data: payload
      });

      if (response.status === 201) {
        const data = response.data
        if (typeof data === 'string') {
          // 백엔드가 ID 문자열만 반환 → 프론트에서 표준 객체로 보정
          return {
            examId: data,
            id: data,
            examName: payload.examName,
            description: payload.description,
            textBooks: payload.textBooks || [],
            startTime: payload.startTime,
            endTime: payload.endTime,
            duration: payload.duration,
            settings: payload.settings,
            questionDistribution: payload.questionDistribution || {},
            status: 'scheduled'
          }
        }
        return data
      }
    } catch (error) {
      console.error('Error creating exam:', error)
      throw error
    }
  }

  // 시험 수정
  async updateExam(examId, examData) {
    try {
      // 종료시간 계산 로직: 로컬 벽시각 문자열 유지
      const startLocal = (examData.startTime || '').toString()
      const addMinutesLocal = (localStr, minutes) => {
        try {
          if (!localStr) return ''
          const [d, t] = localStr.split('T')
          const [yy, mm, dd] = (d || '').split('-').map(n => parseInt(n, 10))
          const [HH, MM = '0'] = (t || '').split(':')
          if (!yy || !mm || !dd || HH == null) return ''
          const base = new Date(yy, (mm || 1) - 1, dd, parseInt(HH, 10) || 0, parseInt(MM, 10) || 0, 0, 0)
          base.setMinutes(base.getMinutes() + Number(minutes || 0))
          const pad = (n) => String(n).padStart(2, '0')
          const y = base.getFullYear()
          const M = pad(base.getMonth() + 1)
          const D = pad(base.getDate())
          const h = pad(base.getHours())
          const m = pad(base.getMinutes())
          return `${y}-${M}-${D}T${h}:${m}`
        } catch { return '' }
      }
      // 정책: duration이 있으면 항상 startTime + duration(분)으로 재계산하여 일관성 확보
      const endLocal = (startLocal && examData?.duration)
        ? addMinutesLocal(startLocal, examData.duration)
        : String(examData.endTime || '')

      const timezone = examData.timeZone || (examData.settings && examData.settings.timeZone) || 'Asia/Seoul'

      // 현지 시간을 UTC로 변환
      const startUtc = formatLocalToUtc(startLocal, timezone)
      const endUtc = formatLocalToUtc(endLocal, timezone)

      const payload = {
        ...examData,
        startTimeUTC: startUtc || startLocal,
        endTimeUTC  : endUtc || endLocal,
        timezone
      }
      // 기존 필드 제거 (백엔드에서 사용 안함)
      delete payload.startTime
      delete payload.endTime

      const response = await this.makeRequest({
        method: 'put',
        url: `/exams/${examId}`,
        api_url: CONF.ACHIEVE_API_URL,
        data: payload
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error updating exam:', error)
      throw error
    }
  }

  // 시험 상태 변경
  async updateExamStatus(examId, status) {
    try {
      const response = await this.makeRequest({
        method: 'patch',
        url: `/exams/${examId}/status`,
        api_url: CONF.ACHIEVE_API_URL,
        data: { status }
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error updating exam status:', error)
      throw error
    }
  }

  // 시험 삭제
  async deleteExam(examId) {
    try {
      const response = await this.makeRequest({
        method: 'delete',
        url: `/exams/${examId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 204 || response.status === 200) {
        return true
      }
    } catch (error) {
      console.error('Error deleting exam:', error)
      throw error
    }
  }

  // 시험 복사
  async copyExam(sourceExamId, newExamName) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: '/exams/copy',
        api_url: CONF.ACHIEVE_API_URL,
        data: {
          sourceExamId,
          newExamName
        }
      });

      if (response.status === 200 || response.status === 201) {
        return response.data
      }
    } catch (error) {
      console.error('Error copying exam:', error)
      throw error
    }
  }

  // 시험 공유 (지정 공유 방식)
  // @param {Object} shareData - { examId: string, recipients: string[] }
  // @returns {string} - 생성된 공유 ID (예: SHARE-20251120150000-5678)
  async shareExam(shareData) {
    try {
      // recipients 배열 검증
      if (!shareData.recipients || !Array.isArray(shareData.recipients) || shareData.recipients.length === 0) {
        throw new Error('공유 대상자(recipients)를 최소 1명 이상 지정해야 합니다.')
      }

      const response = await this.makeRequest({
        method: 'post',
        url: '/exams/share',
        api_url: CONF.ACHIEVE_API_URL,
        data: {
          examId: shareData.examId,
          recipients: shareData.recipients
        }
      });

      if (response.status === 200 || response.status === 201) {
        return response.data
      }
    } catch (error) {
      console.error('Error sharing exam:', error)
      throw error
    }
  }

  // 공유 설정 조회
  async getShareSettings(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/exams/${examId}/share`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error fetching share settings:', error)
      throw error
    }
  }

  // 공유 대상자 수정
  // @param {string} shareId - 공유 레코드 ID
  // @param {string[]} recipients - 새로운 공유 대상자 이메일 목록
  // @returns {Object} - 업데이트된 ExamShareRecord
  async updateShareRecipients(shareId, recipients) {
    try {
      if (!recipients || !Array.isArray(recipients) || recipients.length === 0) {
        throw new Error('공유 대상자(recipients)를 최소 1명 이상 지정해야 합니다.')
      }

      const response = await this.makeRequest({
        method: 'put',
        url: `/exams/share/${shareId}/recipients`,
        api_url: CONF.ACHIEVE_API_URL,
        data: { recipients }
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error updating share recipients:', error)
      throw error
    }
  }

  // [Deprecated] 기존 공유 설정 업데이트 - updateShareRecipients 사용 권장
  async updateShareSettings(examId, shareData) {
    console.warn('updateShareSettings is deprecated. Use updateShareRecipients instead.')
    try {
      const response = await this.makeRequest({
        method: 'put',
        url: `/exams/${examId}/share`,
        api_url: CONF.ACHIEVE_API_URL,
        data: shareData
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error updating share settings:', error)
      throw error
    }
  }

  // 공유 취소 (공유 ID로)
  async revokeShare(shareId) {
    try {
      const response = await this.makeRequest({
        method: 'delete',
        url: `/exams/share/${shareId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200 || response.status === 204) {
        return true
      }
    } catch (error) {
      console.error('Error revoking share:', error)
      throw error
    }
  }

  // 나에게 공유된 시험 목록 조회
  // @returns {ExamShareRecord[]} - 현재 사용자가 recipients에 포함된 공유 레코드 목록
  async fetchSharedExamsToMe() {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: '/exams/shared/to-me',
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
      return []
    } catch (error) {
      // 백엔드 API가 아직 구현되지 않은 경우 조용히 실패
      if (error.response?.status === 500 || error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  // [Deprecated] 공유된 시험 목록 조회 (전체) - fetchSharedExamsToMe 사용 권장
  async fetchSharedExams(params = {}) {
    console.warn('fetchSharedExams is deprecated. Use fetchSharedExamsToMe instead.')
    try {
      // 기존 /exams/shared/all 대신 /exams/shared/to-me 사용
      const response = await this.makeRequest({
        method: 'get',
        url: '/exams/shared/to-me',
        api_url: CONF.ACHIEVE_API_URL,
        params
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      // 백엔드 API가 아직 구현되지 않은 경우 조용히 실패
      if (error.response?.status === 500 || error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  // 내가 공유한 시험 목록 조회
  // @returns {ExamShareRecord[]} - 현재 사용자가 공유한 시험 레코드 목록
  async fetchMySharedExams() {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: '/exams/shared/by-me',
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        // 공유 레코드 목록 그대로 반환 (ExamShareRecord[])
        return response.data || []
      }
      return []
    } catch (error) {
      // 백엔드 API가 아직 구현되지 않은 경우 조용히 실패
      if (error.response?.status === 500 || error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  // 내가 공유한 시험에서 공유 대상자 통계 생성
  // @returns {Object[]} - 사용자별 공유된 시험 수 통계
  async fetchMyShareRecipientsStats() {
    try {
      const shareRecords = await this.fetchMySharedExams()

      // 데이터 변환: recipients 배열을 집계하여 사용자별 통계 생성
      const recipientMap = new Map()

      shareRecords.forEach(share => {
        if (share.recipients && Array.isArray(share.recipients)) {
          share.recipients.forEach(email => {
            if (recipientMap.has(email)) {
              recipientMap.get(email).sharedExamCount++
            } else {
              recipientMap.set(email, {
                odlUserId: email,
                userName: email.split('@')[0],
                userEmail: email,
                sharedExamCount: 1
              })
            }
          })
        }
      })

      return Array.from(recipientMap.values())
    } catch (error) {
      console.error('Error fetching share recipients stats:', error)
      return []
    }
  }

  // 내가 공유한 시험의 공유 대상자 목록 조회
  // @returns {ExamShareRecord[]} - 내가 공유한 시험의 전체 레코드 (recipients 배열에서 공유 대상자 확인 가능)
  async fetchShareRecipients() {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: '/exams/shared/recipients',
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data || []
      }
      return []
    } catch (error) {
      // 백엔드 API가 아직 구현되지 않은 경우 조용히 실패
      if (error.response?.status === 500 || error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  // 공유 시험 수락
  async acceptSharedExam(shareId) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: `/exams/shared/${shareId}/accept`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error accepting shared exam:', error)
      throw error
    }
  }

  // 시험에 연결된 클래스 정보 조회
  async fetchExamClassroom(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/learning/examClassroom/${examId}`,
        api_url: CONF.API_URL
      });

      if (response.status === 200) {
        //console.log('fetchExamClassroom response:', response.data);
        return response.data
      }
    } catch (error) {
      console.error('Error fetching exam classroom:', error)
      return null
    }
  }

  // 시험 응시 학생 목록 조회
  async fetchExamStudents(classroomIdx, examId) {
    try {
      //console.log('fetchExamStudents classroomIdx:', classroomIdx);
      const response = await this.makeRequest({
        method: 'get',
        url: `/learning/examStudents/${classroomIdx}/${examId}`,
        api_url: CONF.API_URL  // 주의: ACHIEVE_API_URL이 아닌 일반 API_URL 사용
      });

      //console.log('fetchExamStudents response:', response.data);

      if (response.status === 200) {
        //console.log('fetchExamStudents response:', response.data);
        return response.data
      }
    } catch (error) {
      console.error('Error fetching exam students:', error)
      // 에러가 발생해도 빈 배열 반환
      return []
    }
  }

  // 대면 시험 출석 정보 조회
  async fetchAttendance(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/exam-answers/attendance/${examId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data || []
      }
    } catch (error) {
      console.error('Error fetching attendance:', error)
      // 에러가 발생해도 빈 배열 반환 (출석 정보가 없을 수도 있음)
      return []
    }
  }

  // 대면 시험 출석 정보 저장
  async saveAttendance(attendanceData) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: '/exam-answers/attendance',
        api_url: CONF.ACHIEVE_API_URL,
        data: attendanceData
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error saving attendance:', error)
      throw error
    }
  }

  // 대면 시험 출석 정보 초기화 (전체 삭제)
  async clearAttendance(examId, classroomIdx) {
    try {
      const response = await this.makeRequest({
        method: 'delete',
        url: '/exam-answers/attendance',
        api_url: CONF.ACHIEVE_API_URL,
        data: {
          exam_id: examId,
          classroom_idx: classroomIdx
        }
      });

      if (response.status === 200 || response.status === 204) {
        return response.data || { success: true }
      }
    } catch (error) {
      console.error('Error clearing attendance:', error)
      throw error
    }
  }

  // 리포트 생성
  async generateReport(examId) {
    try {
      const response = await this.makeRequest({
        method: 'post',
        url: `/reports/generate/${examId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error generating report:', error)
      throw error
    }
  }

  // 시험 리포트 조회
  async fetchExamReport(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/reports/exam/${examId}`,
        api_url: CONF.ACHIEVE_API_URL
      });

      if (response.status === 200) {
        return response.data
      }
    } catch (error) {
      console.error('Error fetching exam report:', error)
      throw error
    }
  }

  // 리포트 다운로드
  async downloadReport(examId) {
    try {
      const response = await this.makeRequest({
        method: 'get',
        url: `/reports/exam/${examId}/download`,
        api_url: CONF.ACHIEVE_API_URL,
        responseType: 'blob'
      });

      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `exam-report-${examId}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        return true
      }
    } catch (error) {
      console.error('Error downloading report:', error)
      throw error
    }
  }

}

// 단일 인스턴스 export
export default new ExamService()